import { useMemo, useState } from "react";
import { ArrowRight, BarChart3, Plus } from "lucide-react";
import { formatCurrency } from "../../../format";
import type { RealEstateCase, TimelineActivity } from "../../../types";
import type { MarketAnalysis } from "../domain/types";
import { useMarketAnalysisContext } from "../sync/useMarketAnalysisSync";
import { MarketAnalysisFlow } from "./MarketAnalysisFlow";
import "../market.css";

export function CaseMarketAnalysisEntry({ caseItem }: { caseItem: RealEstateCase }) {
  const controller = useMarketAnalysisContext();
  const [activeAnalysisId, setActiveAnalysisId] = useState<string>();
  const caseAnalyses = useMemo(
    () => controller.analyses.filter((analysis) => analysis.caseId === caseItem.id).sort((first, second) => second.version - first.version),
    [caseItem.id, controller.analyses]
  );
  const draft = caseAnalyses.find((analysis) => analysis.status === "draft");
  const latestFinalized = caseAnalyses.find((analysis) => analysis.status === "finalized");
  const activeAnalysis = controller.analyses.find((analysis) => analysis.id === activeAnalysisId);

  const createNew = () => {
    const analysis = controller.createDraft(caseItem);
    setActiveAnalysisId(analysis.id);
  };

  return (
    <>
      <section className="market-entry-panel">
        <header>
          <div className="market-entry-icon"><BarChart3 size={19} /></div>
          <div><span>CENOVÁ ANALÝZA TRHU</span><h2>{draft ? `Rozpracovaná analýza v${draft.version}` : latestFinalized ? `Posledná analýza v${latestFinalized.version}` : "Pozícia nehnuteľnosti na trhu"}</h2></div>
        </header>

        {draft ? (
          <button className="market-entry-primary" type="button" onClick={() => setActiveAnalysisId(draft.id)}>
            <span><small>DRAFT</small>Pokračovať v analýze</span><ArrowRight size={18} />
          </button>
        ) : latestFinalized?.resultSnapshot ? (
          <>
            <div className="market-entry-summary">
              <div><span>Dátum</span><strong>{new Intl.DateTimeFormat("sk-SK").format(new Date(latestFinalized.finalizedAt ?? latestFinalized.updatedAt))}</strong></div>
              <div><span>Odporúčaná cena</span><strong>{formatCurrency(latestFinalized.resultSnapshot.recommendedListingPrice)}</strong></div>
              <div><span>Trhové pásmo</span><strong>{formatCurrency(latestFinalized.resultSnapshot.marketLow)} – {formatCurrency(latestFinalized.resultSnapshot.marketHigh)}</strong></div>
              <div><span>Comparables</span><strong>{latestFinalized.comparables.length}</strong></div>
            </div>
            <div className="market-entry-actions">
              <button className="market-entry-primary" type="button" onClick={() => setActiveAnalysisId(latestFinalized.id)}>Otvoriť analýzu <ArrowRight size={18} /></button>
              <button className="market-entry-secondary" type="button" onClick={createNew}><Plus size={17} /> Nová analýza</button>
            </div>
          </>
        ) : (
          <button className="market-entry-primary" type="button" onClick={createNew}>
            <span><small>FISPRO MARKET</small>Porovnať s trhom</span><ArrowRight size={18} />
          </button>
        )}
      </section>

      {activeAnalysis ? <MarketAnalysisFlow analysis={activeAnalysis} controller={controller} onClose={() => setActiveAnalysisId(undefined)} /> : null}
    </>
  );
}

export const marketAnalysisTimelineActivities = (analyses: MarketAnalysis[], caseId: string): TimelineActivity[] =>
  analyses
    .filter((analysis) => analysis.caseId === caseId && analysis.status === "finalized" && analysis.resultSnapshot)
    .map((analysis) => ({
      id: `market-analysis:${analysis.id}:finalized`,
      createdAt: analysis.finalizedAt ?? analysis.updatedAt,
      type: "system",
      title: `Cenová analýza v${analysis.version} dokončená`,
      body: `Trhové pásmo ${formatCurrency(analysis.resultSnapshot?.marketLow ?? 0)} – ${formatCurrency(analysis.resultSnapshot?.marketHigh ?? 0)}. Odporúčaná inzertná cena ${formatCurrency(analysis.resultSnapshot?.recommendedListingPrice ?? 0)}. Použitých ${analysis.comparables.filter((comparable) => comparable.decision !== "excluded").length} porovnaní.`
    }));
